import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { contactsApi } from '../api/contacts';
import { campaignsApi } from '../api/campaigns';
import './DashboardPage.css';

const STAGE_CONFIG = {
  new:         { label: 'New',         color: '#6B7280' },
  contacted:   { label: 'Contacted',   color: '#00D9FF' },
  engaged:     { label: 'Engaged',     color: '#A855F7' },
  opportunity: { label: 'Opportunity', color: '#FFB800' },
  partner:     { label: 'Partner',     color: '#39FF14' },
  inactive:    { label: 'Inactive',    color: '#374151' },
};

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.05 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 260, damping: 22 } },
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentContacts, setRecentContacts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [statsData, contactsData, campaignsData] = await Promise.all([
          contactsApi.getStats(),
          contactsApi.getAll({ limit: 5, page: 1 }),
          campaignsApi.getAll(),
        ]);
        setStats(statsData);
        setRecentContacts(contactsData.contacts || []);
        setCampaigns(campaignsData.campaigns || []);
      } catch (err) {
        console.error('[Dashboard]', err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const campaignTotals = campaigns.reduce(
    (acc, c) => ({
      sent: acc.sent + (c.stats?.sent || 0),
      opened: acc.opened + (c.stats?.opened || 0),
      clicked: acc.clicked + (c.stats?.clicked || 0),
    }),
    { sent: 0, opened: 0, clicked: 0 }
  );

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="dashboard-spinner" />
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-inner">
        <div className="dashboard-header">
          <h1 className="dashboard-title">Pipeline Overview</h1>
          <p className="dashboard-sub">Your CRM at a glance</p>
        </div>

        {/* Pipeline Funnel */}
        <section className="dashboard-section">
          <h2 className="section-title">Pipeline Stages</h2>
          <motion.div
            className="stage-grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {Object.entries(STAGE_CONFIG).map(([stage, cfg]) => (
              <motion.div
                key={stage}
                className="stage-card"
                style={{ '--stage-color': cfg.color }}
                variants={itemVariants}
                whileHover={{ y: -4, boxShadow: `0 8px 30px ${cfg.color}33` }}
                onClick={() => navigate(`/contacts?stage=${stage}`)}
              >
                <div className="stage-count" style={{ color: cfg.color }}>
                  {stats?.stats?.[stage] ?? 0}
                </div>
                <div className="stage-label">{cfg.label}</div>
                <div className="stage-bar">
                  <div
                    className="stage-bar-fill"
                    style={{
                      background: cfg.color,
                      width: `${Math.min(100, ((stats?.stats?.[stage] ?? 0) / Math.max(1, stats?.total ?? 1)) * 100)}%`,
                    }}
                  />
                </div>
              </motion.div>
            ))}
          </motion.div>
        </section>

        <div className="dashboard-row">
          {/* Recent Contacts */}
          <section className="dashboard-section dashboard-section--half">
            <div className="section-header">
              <h2 className="section-title">Recent Contacts</h2>
              <Link to="/contacts" className="section-link">View all</Link>
            </div>
            <div className="recent-list">
              {recentContacts.length === 0 && (
                <div className="empty-state">No contacts yet</div>
              )}
              {recentContacts.map(contact => {
                const stageCfg = STAGE_CONFIG[contact.stage] || STAGE_CONFIG.new;
                const name = contact.company || `${contact.firstName} ${contact.lastName || ''}`.trim();
                const initial = name[0]?.toUpperCase() || '?';
                return (
                  <div
                    key={contact._id}
                    className="recent-item"
                    onClick={() => navigate(`/contacts/${contact._id}`)}
                  >
                    <div
                      className="recent-avatar"
                      style={{ background: `${stageCfg.color}22`, color: stageCfg.color, border: `1px solid ${stageCfg.color}44` }}
                    >
                      {contact.avatarImage
                        ? <img src={contact.avatarImage} alt={name} />
                        : initial}
                    </div>
                    <div className="recent-info">
                      <span className="recent-name">{name}</span>
                      <span className="recent-type">{contact.contactType}</span>
                    </div>
                    <div
                      className="recent-stage-badge"
                      style={{ background: `${stageCfg.color}22`, color: stageCfg.color, border: `1px solid ${stageCfg.color}44` }}
                    >
                      {stageCfg.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Campaign Stats */}
          <section className="dashboard-section dashboard-section--half">
            <div className="section-header">
              <h2 className="section-title">Campaign Stats</h2>
              <Link to="/campaigns" className="section-link">View all</Link>
            </div>
            <motion.div
              className="campaign-stats-grid"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {[
                { label: 'Emails Sent', value: campaignTotals.sent, color: '#FF1493' },
                { label: 'Opened', value: campaignTotals.opened, color: '#A855F7' },
                { label: 'Clicked', value: campaignTotals.clicked, color: '#39FF14' },
                { label: 'Campaigns', value: campaigns.length, color: '#FFB800' },
              ].map(stat => (
                <motion.div
                  key={stat.label}
                  className="campaign-stat-card"
                  style={{ '--stat-color': stat.color }}
                  variants={itemVariants}
                  whileHover={{ y: -4, boxShadow: `0 8px 24px ${stat.color}33` }}
                >
                  <div className="campaign-stat-value" style={{ color: stat.color }}>{stat.value}</div>
                  <div className="campaign-stat-label">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </section>
        </div>
      </div>
    </div>
  );
}
